/**
 * Logic behind the desktop "Sync queue" screen (WP7.8b, ADR-085 Amendment) — pure functions plus two
 * thin orchestrators over `desktop-bridge.ts` so the review rules are unit tested without Vue/Tauri.
 *
 * Review rules (AGENTS.md rule 11 — no Last-Write-Wins, no auto-merge):
 * - a `FAILED` command shows the server's own problem (`lastError`) and stays until the USER acts;
 * - "Discard" deletes it (only `FAILED` may be discarded — enforced again in the Rust store);
 * - "Re-submit as new" queues a brand-new command (fresh UUIDv7 idempotency key) with the same
 *   payload and only then discards the rejected one. It is an explicit user action, never automatic,
 *   and the server re-validates it from scratch (it may fail again — the payload is never rewritten).
 */
import { sendQueuedCommand } from '../api.js';
import {
  discardFailedOutboxCommand,
  enqueueOfflineCommand,
  listOutboxCommands,
  replayOutboxCommands,
  type OutboxCommand,
  type OutboxCommandStatus,
  type OutboxReplaySummary,
} from './desktop-bridge.js';
import { countLinesInCommand, COUNT_COMMAND_TYPE } from './offline-counts.js';

export interface OutboxSummary {
  pending: number;
  sending: number;
  failed: number;
  completed: number;
}

export function summarizeOutbox(commands: readonly OutboxCommand[]): OutboxSummary {
  const summary: OutboxSummary = { pending: 0, sending: 0, failed: 0, completed: 0 };
  for (const c of commands) {
    if (c.status === 'PENDING') summary.pending += 1;
    else if (c.status === 'SENDING') summary.sending += 1;
    else if (c.status === 'FAILED') summary.failed += 1;
    else summary.completed += 1;
  }
  return summary;
}

/** Items the user still has to care about: everything not yet `COMPLETED`, FAILED first, then in the
 * strict replay (creation) order. Completed commands are history, hidden by default. */
export function reviewQueue(commands: readonly OutboxCommand[], includeCompleted = false): OutboxCommand[] {
  const rank: Record<OutboxCommandStatus, number> = { FAILED: 0, SENDING: 1, PENDING: 2, COMPLETED: 3 };
  return commands
    .filter((c) => includeCompleted || c.status !== 'COMPLETED')
    .map((c, index) => ({ c, index }))
    .sort((a, b) => rank[a.c.status] - rank[b.c.status] || a.index - b.index)
    .map((x) => x.c);
}

export function canDiscard(command: Pick<OutboxCommand, 'status'>): boolean {
  return command.status === 'FAILED';
}

export function canResubmit(command: Pick<OutboxCommand, 'status'>): boolean {
  return command.status === 'FAILED';
}

/** i18n key + params describing what a command is, for the queue table. */
export function describeCommand(command: OutboxCommand): { key: string; params: Record<string, number> } {
  if (command.commandType === COUNT_COMMAND_TYPE) {
    return { key: 'desktop.sync.types.countLines', params: { n: countLinesInCommand(command) } };
  }
  return { key: 'desktop.sync.types.other', params: {} };
}

/** Deps injectable for tests. */
export interface QueueDeps {
  enqueue: typeof enqueueOfflineCommand;
  discard: typeof discardFailedOutboxCommand;
}

const defaultDeps: QueueDeps = { enqueue: enqueueOfflineCommand, discard: discardFailedOutboxCommand };

/**
 * Explicit re-submit of a rejected command as a NEW command. Order matters: enqueue first, discard
 * second — if the enqueue fails nothing is lost; if the discard fails the user just sees the old
 * FAILED row remain next to the new one (never a lost observation). Returns the new command.
 */
export async function resubmitAsNew(command: OutboxCommand, deps: QueueDeps = defaultDeps): Promise<OutboxCommand> {
  if (!canResubmit(command)) {
    throw new Error('Only a FAILED command can be re-submitted.');
  }
  let payload: unknown;
  try {
    payload = JSON.parse(command.payloadJson);
  } catch {
    throw new Error('The queued payload is unreadable and cannot be re-submitted; discard it and re-enter the data.');
  }
  const created = await deps.enqueue(command.commandType, command.method, command.endpoint, payload);
  await deps.discard(command.id);
  return created;
}

/** Replays the PENDING queue against the server with each command's own idempotency key. */
export function syncOutboxNow(): Promise<OutboxReplaySummary> {
  return replayOutboxCommands((command) =>
    sendQueuedCommand(command.method, command.endpoint, command.payloadJson, command.idempotencyKey),
  );
}

export async function loadReviewQueue(includeCompleted = false): Promise<OutboxCommand[]> {
  return reviewQueue(await listOutboxCommands(), includeCompleted);
}
